import { useEffect, useState } from "react";
import api from "../api/axios";
import Layout from "../components/Layout";
import Card from "../components/UI/Card";
import Input from "../components/UI/Input";
import Pagination from "../components/UI/Pagination";
import { useNavigate } from "react-router-dom";

function LoadingSpinner() {
  return (
    <tr>
      <td colSpan={999}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: "20px" }}>
          <div style={{ position: "relative", width: 56, height: 56 }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid rgb(var(--border))" }} />
            <div
              className="animate-spin"
              style={{ position: "absolute", inset: 0, borderRadius: "50%", border: "3px solid transparent", borderTopColor: "rgb(var(--primary))", borderRightColor: "rgb(var(--primary))" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgb(var(--primary))", opacity: 0.7 }}>
              Loading Records...
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  className="animate-bounce"
                  style={{ width: 6, height: 6, borderRadius: "50%", background: "rgb(var(--primary))", opacity: 0.4, animationDelay: `${i * 0.18}s`, display: "inline-block" }}
                />
              ))}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

const COLUMNS = [
  { label: "CUSTOMER NAME",     key: "CUSTOMER_NAME" },
  { label: "CUSTOMER MOBILE",   key: "CUSTOMER_MOBILE_NO" },
  { label: "STATUS",            key: "STATUS" },
  { label: "DEALER CODE",       key: "DEALER_CODE" },
  { label: "TC NAME",           key: "TC_NAME" },
  { label: "DEALER STATE",      key: "DEALER_STATE" },
  { label: "DEALER CITY",       key: "DEALER_CITY" },
  { label: "ZONE",              key: "Zone" },
  { label: "REGION",            key: "Region" },
  { label: "CLAIM STATUS",      key: "ClaimStatus" },
  { label: "RM NAME",           key: "RM_Name" },
  { label: "POLICY TYPE",       key: "Policy_Type" },
  { label: "POLICY NO",         key: "POLICY_NO" },
  { label: "POLICY START DATE", key: "POLICY_START_DATE" },
  { label: "OD POLICY EXPIRY",  key: "OD_POLICY_EXPIRY_DATE" },
  { label: "TP POLICY EXPIRY",  key: "TPPOLICY_EXPIRY_DATE" },
  { label: "OTHER TP EXPIRY",   key: "OTHER_TP_POLICY_EXPIRY_DATE" },
  { label: "OTHER TP POLICY NO",key: "OTHERTP_POLICY_NO" },
  { label: "INVOICE DATE",      key: "INVOICE_DATE" },
  { label: "REGISTRATION NO",   key: "Registration_No" },
  { label: "COVER TYPE",        key: "CoverType" },
  { label: "MODEL NAME",        key: "MODEL_NAME" },
  { label: "VEHICLE CLASS",     key: "VehicleClass" },
  { label: "MFG YEAR",          key: "MFG_YEAR" },
  { label: "TP TENURE",         key: "Tp_Tenure" },
  { label: "CHASSIS NO",        key: "CHASSIS_NO" },
  { label: "DOB",               key: "DOB" },
  { label: "CUSTOMER EMAIL",    key: "CUSTOMER_EMAIL_ID" },
  { label: "INSURANCE COMPANY", key: "INSURANCE_COMPANY" },
  { label: "ALTERNATE NO 1",    key: "ALTERNATE_NO 1" },
  { label: "ALTERNATE NO 2",    key: "ALTERNATE_NO 2" },
  { label: "REMARK",            key: "REMARK" },
];

const DATE_KEYS = new Set([
  "POLICY_START_DATE", "OD_POLICY_EXPIRY_DATE", "TPPOLICY_EXPIRY_DATE",
  "OTHER_TP_POLICY_EXPIRY_DATE", "INVOICE_DATE", "DOB", "REMARK_DATE", "follow_up_date"
]);

function toDisplayDate(v) {
  if (!v) return "";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const mname = /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/.exec(s);
  if (mname) {
    const M = { Jan:"01",Feb:"02",Mar:"03",Apr:"04",May:"05",Jun:"06",Jul:"07",Aug:"08",Sep:"09",Oct:"10",Nov:"11",Dec:"12" };
    if (M[mname[1]]) return `${mname[2].padStart(2,"0")}/${M[mname[1]]}/${mname[3]}`;
  }
  const parts = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (parts) return `${parts[1].padStart(2,"0")}/${parts[2].padStart(2,"0")}/${parts[3]}`;
  return s;
}

const STATUS_COLORS = {
  "INTRESTED":        { bg: "#dcfce7", color: "#15803d" },
  "NOT INTRESTED":    { bg: "#fee2e2", color: "#dc2626" },
  "NOT PICKING CALL": { bg: "#fef9c3", color: "#a16207" },
  "NOT CONTACTED":    { bg: "#f1f5f9", color: "#475569" },
};

const FILTER_DEFS = [
  { key: "status",      label: "Status",       dataKey: "STATUS" },
  { key: "tcName",      label: "TC Name",      dataKey: "TC_NAME" },
  { key: "dealerCode",  label: "Dealer Code",  dataKey: "DEALER_CODE" },
  { key: "zone",        label: "Zone",         dataKey: "Zone" },
  { key: "claimStatus", label: "Claim Status", dataKey: "ClaimStatus" },
  { key: "policyType",  label: "Policy Type",  dataKey: "Policy_Type" },
];

const EMPTY_FILTERS = Object.fromEntries(FILTER_DEFS.map(f => [f.key, ""]));

export default function RecordsList() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterOptions, setFilterOptions] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  async function load() {
    setLoading(true);
    try {
      const params = { q, page, limit };
      for (const [k, v] of Object.entries(filters)) { if (v) params[k] = v; }
      const { data } = await api.get("/records", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }

  // Fetch distinct values for filter dropdowns
  useEffect(() => {
    async function loadOptions() {
      try {
        const { data } = await api.get("/records/filter-options");
        setFilterOptions(data);
      } catch {}
    }
    loadOptions();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); load(); }, 400);
    return () => clearTimeout(timer);
    /* eslint-disable-next-line */
  }, [q, filters]);

  return (
    <Layout>
      <div className="flex flex-col gap-5 h-full">

        {/* ── Page header ── */}
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: "rgb(var(--primary))", opacity: 0.7 }}>
              Management
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Records</h1>
          </div>
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold"
            style={{ background: "rgba(var(--primary),0.08)", color: "rgb(var(--primary))" }}
          >
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "rgb(var(--primary))" }} />
            {total.toLocaleString()} Total
          </div>
        </div>

        {/* ── Search bar + Filters ── */}
        <Card className="p-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, policy, chassis, mobile..."
                className="pl-9"
              />
            </div>
            <button
              onClick={() => setShowFilters(p => !p)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors whitespace-nowrap"
              style={showFilters || activeFilterCount
                ? { background: "rgb(var(--primary))", color: "white", borderColor: "rgb(var(--primary))" }
                : { borderColor: "rgb(var(--border))", color: "rgb(var(--fg))" }
              }
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
            </button>
            <button
              onClick={() => { setPage(1); load(); }}
              className="px-5 py-2 rounded-xl text-sm font-semibold text-white hover:opacity-90 transition-opacity whitespace-nowrap"
              style={{ background: "rgb(var(--primary))" }}
            >
              Search
            </button>
          </div>

          {/* Filter dropdowns */}
          {showFilters && (
            <div className="mt-3 pt-3 border-t border-[rgb(var(--border))]">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                {FILTER_DEFS.map(f => (
                  <div key={f.key}>
                    <label className="block text-[10px] font-bold uppercase tracking-wider opacity-40 mb-1">{f.label}</label>
                    <select
                      value={filters[f.key]}
                      onChange={(e) => setFilters(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-2.5 py-1.5 rounded-lg border text-xs bg-[rgb(var(--card))] border-[rgb(var(--border))] text-[rgb(var(--fg))] outline-none focus:border-[rgb(var(--primary))]"
                    >
                      <option value="">All</option>
                      {(filterOptions[f.key] || []).map(v => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {activeFilterCount > 0 && (
                <button
                  onClick={() => setFilters(EMPTY_FILTERS)}
                  className="mt-2 text-xs font-semibold opacity-50 hover:opacity-100 transition-opacity"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </Card>

        {/* ── Table card ── */}
        <Card className="overflow-hidden flex flex-col min-h-0">
          {/* Table scroll area */}
          <div className="overflow-auto flex-1">
            <table
              className="w-full text-sm border-collapse"
              style={{ minWidth: `${COLUMNS.length * 148 + 90}px` }}
            >
              <thead className="sticky top-0 z-10">
                <tr style={{ background: "rgb(var(--primary))" }}>
                  <th className="text-left px-4 py-3 text-white text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-r border-white/10">
                    #
                  </th>
                  <th className="text-left px-4 py-3 text-white text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-r border-white/10">
                    ACTION
                  </th>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="text-left px-4 py-3 text-white text-[11px] font-bold uppercase tracking-wider whitespace-nowrap border-r border-white/10"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSpinner />
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 2} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 opacity-40">
                        <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414A1 1 0 0 1 19 9.414V19a2 2 0 0 1-2 2z" />
                        </svg>
                        <span className="text-sm font-semibold">No records found</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((r, idx) => {
                    const status = r.data?.["STATUS"];
                    const statusStyle = STATUS_COLORS[status] || {};
                    return (
                      <tr
                        key={r._id}
                        className="border-b border-[rgb(var(--border))] hover:bg-[rgba(var(--primary),0.04)] transition-colors"
                      >
                        <td className="px-4 py-2.5 text-xs opacity-30 font-mono whitespace-nowrap border-r border-[rgb(var(--border))]">
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className="px-4 py-2.5 border-r border-[rgb(var(--border))] cursor-pointer">
                          <button
                            onClick={() => nav(`/records/${r._id}`)}
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white hover:opacity-80  transition-opacity"
                            style={{ background: "rgb(var(--primary))" }}
                          >
                            Edit
                          </button>
                        </td>
                        {COLUMNS.map((col) => {
                          const val = r.data?.[col.key];
                          if (col.key === "STATUS" && val) {
                            return (
                              <td key={col.key} className="px-4 py-2.5 whitespace-nowrap border-r border-[rgb(var(--border))] cursor-pointer">
                                <span
                                  className="px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                                  style={{ background: statusStyle.bg || "#f1f5f9", color: statusStyle.color || "#475569" }}
                                >
                                  {val}
                                </span>
                              </td>
                            );
                          }
                          const display = DATE_KEYS.has(col.key) ? toDisplayDate(val) : (val || "");
                          return (
                            <td key={col.key} className="px-4 py-2.5 text-sm border-r border-[rgb(var(--border))]">
                              <div className="max-w-[180px] truncate" title={display || ""}>
                                {display || <span className="opacity-20">—</span>}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination strip */}
          <div className="px-5 py-3 border-t border-[rgb(var(--border))]" style={{ background: "rgba(var(--primary),0.02)" }}>
            <Pagination page={page} total={total} limit={limit} onChange={setPage} />
          </div>
        </Card>
      </div>
    </Layout>
  );
}
